import React, { FC } from 'react'
import styled, { keyframes } from 'styled-components'
import { Fonts } from 'components/base-ui'
import { getResizableURL } from 'helpers/image'
import useLanguage from 'hooks/useLanguage'
import { useTranslation } from 'react-i18next'
import { BuffetPackageProducts } from 'hooks/buffet/useCategory'

type Props = {
  product: BuffetPackageProducts[number]
  onClick: (product: BuffetPackageProducts[number]) => void
}

const ExcludedProduct: FC<Props> = ({ product, onClick }) => {
  const { t } = useTranslation()
  const { getPropertyByLang } = useLanguage()
  const productImg1x = getResizableURL(product.images, { width: '250', height: '250_fmt-jpg' })
  const productImg2x = getResizableURL(product.images, { width: '330', height: '330_fmt-jpg' })
  const productImg3x = getResizableURL(product.images, { width: '375', height: '375_fmt-jpg' })

  return (
    <Wrpapper onClick={() => onClick(product)}>
      <ThumbnailWrapper>
        <Thumbnail image1x={productImg1x} image2x={productImg2x} image3x={productImg3x} />
        <ExcludedLabel>{t('buffet.productListPage.excluded')}</ExcludedLabel>
      </ThumbnailWrapper>
      {product.buffetPackageProducts.length > 0 && (
        <Packages key={product.id}>
          {product.buffetPackageProducts.map((item) => (
            <Label key={item.id}>
              {getPropertyByLang({ th: item.buffetPackage.nameTh, en: item.buffetPackage.nameEn })}
            </Label>
          ))}
        </Packages>
      )}
      <Name>{getPropertyByLang({ th: product.nameTh, en: product.nameEn })}</Name>
    </Wrpapper>
  )
}

export default ExcludedProduct

const Wrpapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 48%;
`
const ThumbnailWrapper = styled.div`
  position: relative;
  width: 100%;
  border-radius: 8px;

  &::before {
    content: '';
    display: block;
    padding-top: 100%;
  }
`
const FadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`

const Thumbnail = styled.div<{ image1x: string; image2x: string; image3x: string }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: 8px;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  animation: ${FadeIn} 0.6s forwards;
  background-image: -webkit-image-set(
    url(${({ image1x }) => `'${image1x}'`}) 1x,
    url(${({ image2x }) => `'${image2x}'`}) 2x,
    url(${({ image3x }) => `'${image3x}'`}) 3x
  );
  background-image: image-set(
    url(${({ image1x }) => `'${image1x}'`}) 1x,
    url(${({ image2x }) => `'${image2x}'`}) 2x,
    url(${({ image3x }) => `'${image3x}'`}) 3x
  );
`

const Label = styled(Fonts.SmallTitleRegular)`
  font-size: 8px;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.primary};
  padding: 2px 4px;
  border-radius: 99px;
`

const ExcludedLabel = styled(Label)`
  z-index: 99;
  position: absolute;
  top: 12px;
  left: 12px;
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.errorDark};
`

const Packages = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: 5px;
  column-gap: 8px;
  padding-top: 15px;
  padding-bottom: 5px;
`

const Name = styled(Fonts.H4)`
  font-size: 15px;
`
